<script setup lang="ts">
/**
 * Common DialogModal Component
 *
 * A reusable PrimeVue Dialog wrapper with Tailwind CSS styling.
 * Provides consistent header, body, and footer layout via props and slots.
 */
import { computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import IconComponent from '../icons/IconComponent.vue';
import { useSharedTranslation } from '../../composables/useTranslationProvider';

export interface FooterButton {
  label: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';
  class?: string;
  disabled?: boolean;
  loading?: boolean;
  action: 'submit' | 'cancel' | string;
}

const props = withDefaults(
  defineProps<{
    /** Controls dialog visibility (v-model) */
    visible: boolean;
    /** Dialog title displayed in the header */
    title: string;
    /** Position of the dialog */
    position?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
    /** Whether the dialog is modal (has backdrop) */
    modal?: boolean;
    /** Whether the dialog is draggable */
    draggable?: boolean;
    /** Maximum width class (Tailwind) */
    maxWidth?: string;
    /** Maximum height (CSS value, e.g. "90vh") applied to the dialog root */
    maxHeight?: string;
    /** Footer buttons configuration */
    footerButtons?: FooterButton[];
    /** Whether to show the close (X) button in header */
    showClose?: boolean;
    /** Whether to show the footer */
    showFooter?: boolean;
    /**
     * When true the default body padding (px-6 py-4) is removed, letting
     * the consumer control its own padding via the default slot.
     */
    noPadding?: boolean;
    /**
     * When true the close button emits `close` but does NOT emit
     * `update:visible(false)` -- the consumer is responsible for closing.
     * Use this when you need a guard (e.g. unsaved-data confirmation)
     * before actually dismissing the dialog.
     */
    interceptClose?: boolean;
    /** Accessible label for the close (X) button — defaults to the app-provided translation, override only if needed. */
    closeButtonLabel?: string;
  }>(),
  {
    position: 'top',
    modal: true,
    draggable: false,
    maxWidth: 'max-w-4xl',
    showClose: true,
    showFooter: true,
    footerButtons: () => [],
    noPadding: false,
    interceptClose: false,
    maxHeight: undefined,
    closeButtonLabel: undefined,
  },
);

const { t } = useSharedTranslation();
const resolvedCloseButtonLabel = computed(() => props.closeButtonLabel ?? t('common.close', 'Close'));

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'close'): void;
  (e: 'action', action: string): void;
}>();

function close() {
  emit('close');
  if (!props.interceptClose) {
    emit('update:visible', false);
  }
}

/**
 * Handles PrimeVue Dialog's internal update:visible event (triggered by
 * Escape key, mask click, etc.). When interceptClose is true we route
 * hide requests through close() so the consumer can guard dismissal.
 */
function onDialogVisibleUpdate(value: boolean) {
  if (!value && props.interceptClose) {
    close();
    return;
  }
  emit('update:visible', value);
}

function handleAction(action: string) {
  if (action === 'cancel') {
    close();
  } else {
    emit('action', action);
  }
}

const maxWidthMap: Record<string, string> = {
  'max-w-sm': '24rem',
  'max-w-md': '28rem',
  'max-w-lg': '32rem',
  'max-w-xl': '36rem',
  'max-w-2xl': '42rem',
  'max-w-3xl': '48rem',
  'max-w-4xl': '56rem',
  'max-w-5xl': '64rem',
  'max-w-6xl': '72rem',
  'max-w-7xl': '80rem',
  'max-w-full': 'calc(100vw - 2rem)',
};

const resolvedMaxWidth = computed(() => maxWidthMap[props.maxWidth] ?? '50rem');

const dialogPT = computed(() => ({
  root: {
    class: 'bg-white rounded-xl shadow-2xl p-0 border border-gray-200 w-full flex flex-col',
    style: [
      `max-width: ${resolvedMaxWidth.value}; width: 100%;`,
      props.maxHeight ? `max-height: ${props.maxHeight};` : '',
    ].filter(Boolean).join(' '),
  },
  header: { class: 'shrink-0 !p-0' },
  content: {
    class: [
      'text-gray-700',
      props.noPadding ? '!p-0' : '',
      props.maxHeight ? 'flex-1 !overflow-hidden min-h-0 flex flex-col' : '',
    ].filter(Boolean).join(' '),
  },
  footer: { class: 'shrink-0 !p-0' },
}));
</script>

<template>
  <Dialog
    :visible="visible"
    :modal="modal"
    :position="position"
    :draggable="draggable"
    :closable="false"
    :pt="dialogPT"
    @update:visible="onDialogVisibleUpdate"
  >
    <!-- Header -->
    <template #header>
      <div class="w-full border-b border-gray-200 flex items-center justify-between px-6 py-2">
        <slot name="header">
          <span class="text-lg text-gray-900 font-medium not-italic">{{ title }}</span>
        </slot>
        <Button
          v-if="showClose"
          text
          rounded
          severity="secondary"
          class="ml-2 p-2!"
          :aria-label="resolvedCloseButtonLabel"
          data-testid="dialog-close-btn"
          @click="close"
        >
          <template #icon>
            <IconComponent
              name="Close"
              size-class="h-5 w-5"
              color="text-gray-600"
            />
          </template>
        </Button>
      </div>
    </template>

    <!-- Body -->
    <div
      :class="[
        noPadding ? '' : 'px-6 py-4',
        maxHeight ? 'flex-1 min-h-0 overflow-hidden flex flex-col' : '',
      ]"
    >
      <slot />
    </div>

    <!-- Footer -->
    <template
      v-if="showFooter"
      #footer
    >
      <div class="w-full border-t border-gray-200 px-6 py-4 flex justify-start gap-2">
        <slot name="footer">
          <Button
            v-for="btn in footerButtons"
            :key="btn.action"
            :label="btn.label"
            :severity="btn.severity ?? 'primary'"
            :class="btn.class"
            :disabled="btn.disabled"
            :loading="btn.loading"
            @click="handleAction(btn.action)"
          />
        </slot>
      </div>
    </template>
  </Dialog>
</template>
